import { RegistrationFieldProps } from '../../../RegistrationField.types';
import React, {
  ForwardedRef,
  forwardRef,
  memo,
  useCallback,
  useState,
} from 'react';
import { Box, FlatSelect, Text, TextInput } from '@betty/moonlight';
import { useDebouncedAddressSearch } from '@betty/details-attestation';
import { retrieveAddress } from '@betty/toolkit/api';
import { useDispatch } from '@betty/toolkit/state';
import { getAddressSelectableOptions } from '@betty/navigation/lib/bottomSheets/AddressSearch/utils/getAddressSelectableOptions';

function AutoAddressField(
  { value, error, onValue }: RegistrationFieldProps<'address'>,
  ref: ForwardedRef<TextInput>,
) {
  const dispatch = useDispatch();
  const [addressOptions, setAddressOptions] = useState([]);
  const [selectableOptionsMap, setSelectableOptionsMap] = useState(new Map());
  const { performDebouncedAddressSearch } = useDebouncedAddressSearch();
  const [hasSearchQuery, setHasSearchQuery] = useState(false);

  const handleSearchChange = useCallback(
    async (searchQuery: string) => {
      if (searchQuery.length < 1) {
        setHasSearchQuery(false);
        setAddressOptions([]);
        setSelectableOptionsMap(new Map());

        onValue({
          suggestionSelected: false,
          useAutoAddress: true,
          autocompleteAddress: '',
          addressContainer: undefined,
          streetName: '',
          unit: '',
          buildingNumber: '',
          city: '',
          postalCode: '',
          country: 'Canada',
          countryCode: 'CA',
          province: 'Ontario',
          provinceCode: 'ON',
        });
        return;
      } else {
        setHasSearchQuery(true);
        const result = await performDebouncedAddressSearch(searchQuery);
        const selectableSuggestions = getAddressSelectableOptions(result);

        const newSelectableOptionsMap = new Map();

        const flatSelectOptions = selectableSuggestions.map((suggestion) => {
          const id = suggestion.dynamicValues?.Id || suggestion.text;

          newSelectableOptionsMap.set(suggestion.text, suggestion);

          return {
            id,
            label: suggestion.text,
          };
        });

        setSelectableOptionsMap(newSelectableOptionsMap);
        setAddressOptions(flatSelectOptions);
      }
    },
    [performDebouncedAddressSearch, onValue],
  );

  const handleSuggestionSelect = useCallback(
    async (selectedOptionText: string) => {
      const selectableOptionItem = selectableOptionsMap.get(selectedOptionText);

      if (!selectableOptionItem) {
        onValue({
          suggestionSelected: false,
          useAutoAddress: true,
          autocompleteAddress: '',
          addressContainer: undefined,
          streetName: '',
          unit: '',
          buildingNumber: '',
          city: '',
          postalCode: '',
          country: 'Canada',
          countryCode: 'CA',
          province: 'Ontario',
          provinceCode: 'ON',
        });
        return;
      }

      if (selectableOptionItem.dynamicValues?.Type === 'Container') {
        const suggestions = await performDebouncedAddressSearch(
          selectableOptionItem.dynamicValues?.Text,
          selectableOptionItem.dynamicValues?.Id,
        );
        const selectableSuggestions = getAddressSelectableOptions(suggestions);

        const newSelectableOptionsMap = new Map();

        const flatSelectOptions = selectableSuggestions.map((suggestion) => {
          const id = suggestion.dynamicValues?.Id || suggestion.text;

          newSelectableOptionsMap.set(suggestion.text, suggestion);

          return {
            id,
            label: suggestion.text,
          };
        });

        setSelectableOptionsMap(newSelectableOptionsMap);
        setAddressOptions(flatSelectOptions);
        return;
      }

      const { payload: retrievedFullAddress } = await dispatch(
        retrieveAddress(selectableOptionItem.dynamicValues?.Id || ''),
      );

      const {
        CountryName = '',
        CountryIso2 = '',
        Line1 = '',
        PostalCode = '',
        City = '',
        SubBuilding = '',
        BuildingNumber = '',
        Street = '',
        ProvinceCode = '',
        ProvinceName = '',
      } = retrievedFullAddress.Items[0];

      onValue({
        suggestionSelected: true,
        useAutoAddress: true,
        autocompleteAddress: `${Line1}, ${City}, ${ProvinceName}, ${PostalCode}`,
        addressContainer: value.addressContainer,
        country: CountryName,
        countryCode: CountryIso2,
        streetName: Street,
        unit: SubBuilding,
        buildingNumber: BuildingNumber,
        city: City,
        province: ProvinceName,
        provinceCode: ProvinceCode,
        postalCode: PostalCode,
      });
    },
    [
      selectableOptionsMap,
      value?.addressContainer,
      onValue,
      dispatch,
      performDebouncedAddressSearch,
    ],
  );

  return (
    <Box>
      <Box marginBottom="space-400">
        <FlatSelect
          scrollEnabled={true}
          hasSearch={true}
          useDynamicOptions={true}
          minSearchLength={1}
          headerNS="registration"
          headerKey="addressLabel"
          value={value?.autocompleteAddress}
          onValue={handleSuggestionSelect}
          onSearchChange={handleSearchChange}
          columns={1}
          options={addressOptions}
          error={error}
          ref={ref}
        />
        {!hasSearchQuery && (
          <Text
            variant="body-s"
            color="darkSurfaceSecondary"
            locNS="registration"
            locKey="addressFooter"
            marginTop="space-200"
          />
        )}
      </Box>
    </Box>
  );
}

export default memo(forwardRef(AutoAddressField));
